import {
  forwardRef,
  useState,
  ReactElement,
  Ref,
  ChangeEvent,
  useEffect,
} from "react";
import { Box, HelpText, Input, Label } from "@twilio-paste/core";

import { constants, copy, types } from "./definitions";

const CompositeInput = forwardRef(
  (
    {
      id,
      characterLimit,
      errorText,
      hasHtmlInLabel,
      helpText,
      inputWidth,
      label,
      // Paste recommendation: https://paste.twilio.design/components/textarea/#positioning-form-fields
      marginBottom = "space80",
      onChange,
      required,
      type,
      value,
      width,
      ...rest
    }: types.CompositeInputProps,
    ref: Ref<HTMLInputElement>
  ): ReactElement => {
    const [characterCount, updateCharacterCount] = useState<number>(0);
    const [hasCharacterCountError, toggleHasCharacterCountError] =
      useState<boolean>(false);

    // Set the character count on load if a starting value is passed
    useEffect(() => {
      if (value?.length) {
        updateCharacterCount(value.length);
      }
    }, []);

    useEffect(() => {
      if (characterLimit && characterCount > characterLimit) {
        toggleHasCharacterCountError(true);
      }

      if (
        hasCharacterCountError &&
        characterLimit &&
        characterCount <= characterLimit
      ) {
        toggleHasCharacterCountError(false);
      }
    }, [characterCount]);

    const hasInputError = rest.hasError || hasCharacterCountError;
    const errorTextId =
      rest.hasError && rest.name
        ? `${rest.name}${constants.ERROR_TEXT_ID_TAG}`
        : "";
    const helpTextId =
      helpText && rest.name ? `${rest.name}${constants.HELP_TEXT_ID_TAG}` : "";

    const renderHelpText = (): string => {
      // Show character limit error
      if (hasCharacterCountError && characterLimit) {
        return copy.errors.characterLimit(characterLimit, characterCount);
      }

      // Show input error
      if (rest.hasError) {
        return errorText || copy.errors.default(rest.name);
      }

      // Show help text with character limit
      if (helpText && characterLimit) {
        return `${helpText} (${copy.helpText.characterLimit(
          characterLimit,
          characterCount
        )})`;
      }

      // Show character limit (no help text)
      if (characterLimit) {
        return copy.helpText.characterLimit(characterLimit, characterCount);
      }

      /**
       * Function will only be called if helpText || characterLimit
       * Once called, if the above checks don't pass, this will be an
       * implict return.
       */
      return helpText!;
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
      updateCharacterCount(event.target.value.length);

      if (onChange) {
        onChange(event);
      }
    };

    return (
      <Box marginBottom={marginBottom} width={width}>
        <Label htmlFor={id} required={required}>
          {hasHtmlInLabel ? (
            <span dangerouslySetInnerHTML={{ __html: label }} />
          ) : (
            label
          )}
        </Label>

        <Box as="div" width={inputWidth}>
          <Input
            aria-describedby={`${errorTextId} ${errorTextId}`}
            id={id}
            onChange={characterLimit ? handleChange : onChange}
            type={type}
            value={value}
            ref={ref}
            hasError={hasInputError}
            {...rest}
          />
        </Box>

        {(hasInputError || helpText || characterLimit) && (
          <HelpText
            id={hasInputError ? errorTextId : helpTextId}
            variant={hasInputError ? "error" : "default"}
          >
            {renderHelpText()}
          </HelpText>
        )}
      </Box>
    );
  }
);

export default CompositeInput;
